import React, { FC, useState } from 'react'

import { useApolloClient } from '@apollo/client'

import { Switch } from '@style-port/roov-ui-components-react'
import { Icon, StandardButton, Txt } from 'src/components/atoms'
import { AssignMailCheckbox, CompassRoleDropdown } from 'src/components/molecules'
import { AssigningProjectSearch } from 'src/components/organisms'

import { useToast, useUser } from 'src/hooks'
import { useGetCompassRoles } from 'src/hooks/useGetCompassRoles'
import { ProjectEntity } from 'src/types'

type Props = {
  targetUserIds: number[]
  isExternal: boolean
  onCancel: () => void
  afterAssigned?: () => void
  className?: string
}

export const UsersBulkAssign: FC<Props> = ({
  targetUserIds,
  isExternal,
  onCancel,
  afterAssigned,
  className,
}) => {
  const [selectedProjects, setSelectedProjects] = useState<ProjectEntity[]>([])
  const [selectedCompassRoleId, setSelectedCompassRoleId] = useState<number | undefined>()
  const [isLoading, setIsLoading] = useState(false)
  const [isSendAssignEmail, setIsSendAssignEmail] = useState(false)

  const { compassRoles, loading: isCompassRoleLoading } = useGetCompassRoles()
  const { assignUsersProjects } = useUser()
  const { displayStandardTextToast, displayErrorTextToast } = useToast()

  const client = useApolloClient()

  const handleOnSelectAlreadyProject = (selectedProject: ProjectEntity) => {
    const updatedProjects = selectedProjects.filter(project => project.id !== selectedProject.id)
    setSelectedProjects(updatedProjects)
  }

  const handleOnClickSubmit = async () => {
    setIsLoading(true)

    const compassRoleId = compassRoles.find(
      compassRole => compassRole.id === selectedCompassRoleId
    )?.id

    if (!compassRoleId) {
      displayErrorTextToast('システム内部のエラーが起きました。再度お試し下さい')
      setIsLoading(false)
      return
    }

    try {
      await assignUsersProjects({
        users: targetUserIds.map(id => ({ id, isExternal })),
        projectIds: selectedProjects.map(project => project.id),
        isSendAssignEmail,
        compassRoleId,
      })

      // プロジェクト一覧・詳細など広範囲に及ぶ処理のためキャッシュをリセットする。リセットを行うと自動的に画面に必要なqueryが再度実行される
      await client.resetStore()

      displayStandardTextToast('プロジェクトのアサインが完了しました。')

      afterAssigned && afterAssigned()
    } catch {
      displayErrorTextToast('プロジェクトアサイン時にエラーが起きました。')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={className}>
      <AssigningProjectSearch
        alreadyAssignedProjectIds={selectedProjects.map(project => project.id)}
        onSelectProject={selectedProject =>
          setSelectedProjects(previous => [...previous, selectedProject])
        }
      />

      {selectedProjects.length > 0 && (
        <div className="tw-bg-surface-secondary tw-p-s0 tw-rounded-[0.6rem] tw-mt-s2">
          <ul className="tw-flex tw-flex-wrap tw-gap-r-1">
            {selectedProjects.map(project => (
              <li key={project.id} className="tw-w-fit">
                <Switch onClick={() => handleOnSelectAlreadyProject(project)}>
                  <div className="tw-flex tw-items-center">
                    <div>{project.name}</div>
                    <Icon path="close/soft/line" size="--r1" className="tw-ml-s-3" />
                  </div>
                </Switch>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="tw-flex tw-items-center tw-mt-s2">
        <Txt className="tw-text-standardText-secondary">ROOV compassの権限</Txt>
        {!isCompassRoleLoading && (
          <CompassRoleDropdown
            compassRoles={compassRoles}
            value={selectedCompassRoleId}
            selectOption={v => setSelectedCompassRoleId(v)}
            size="m"
            className="tw-ml-s2 tw-w-[17.3rem]"
          />
        )}
      </div>

      <AssignMailCheckbox
        isSendAssignEmail={isSendAssignEmail}
        updateCheck={updateValue => setIsSendAssignEmail(updateValue)}
        className="tw-w-fit tw-mx-auto tw-mt-s3"
      />

      <div className="tw-centering tw-mt-s3">
        <StandardButton
          type="button"
          buttonType="secondary"
          text="キャンセル"
          prependIconPath="close/soft/line"
          size="l"
          onClick={onCancel}
        />
        <StandardButton
          size="l"
          text="決定"
          prependIconPath="check/soft/line"
          disabled={selectedProjects.length === 0 || !selectedCompassRoleId}
          isLoading={isLoading}
          onClick={handleOnClickSubmit}
          className="tw-ml-s2"
        />
      </div>
    </div>
  )
}
